import React, { useCallback, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from '@mui/material';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from '../../../MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import { ActivityType, ApprovalLevelType } from 'store/enums/BallotReviewTypes';
import { ApprovalLevel } from 'store/types/Meeting';
import { approvalLevelTypeLabel, meetingSessionToActivity } from 'util/meeting';
import { defaultSnackbarErrorProps } from 'util/layout';

import commonStyles from 'styles/common.module.scss';

const ApprovalLevelSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [statement, setStatement] = useState<ApprovalLevelType | undefined>();

  const currentApprovalLevel: ApprovalLevel = useMemo(() => {
    return {
      statement: statement ? approvalLevelTypeLabel[statement] : '',
    };
  }, [statement]);

  const handleDisplayApprovalLevel = useCallback(async () => {
    try {
      if (!statement) {
        enqueueSnackbar('Approval level is required', { variant: 'warning' });
        return;
      }

      const updatedSession = await MeetingService.saveMeetingSession(
        meetingSession?.currentDocument
          ? {
              ...meetingSession,
              meetingId,
              currentApprovalLevel,
              currentDocument: {
                ...meetingSession?.currentDocument,
                lineItemNumber: undefined,
                lineItemTitle: undefined,
              },
              currentReject: undefined,
              currentTechnicalIssue: undefined,
              currentComment: undefined,
              currentMotion: undefined,
              currentVotingSession: undefined,
            }
          : {
              ...meetingSession,
              meetingId,
              currentApprovalLevel,
              currentReject: undefined,
              currentTechnicalIssue: undefined,
              currentComment: undefined,
              currentMotion: undefined,
              currentVotingSession: undefined,
            }
      );

      logActivity?.({
        ...meetingSessionToActivity(updatedSession),
        type: ActivityType.Approval,
        approvalLevel: updatedSession.currentApprovalLevel,
      });

      enqueueSnackbar(`Displayed approval level`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to display approval level`, defaultSnackbarErrorProps);
      console.error('Error displaying approval level:', error);
    }
  }, [currentApprovalLevel, enqueueSnackbar, logActivity, meetingId, meetingSession, statement]);

  const handleClearApprovalLevel = useCallback(async () => {
    try {
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentApprovalLevel: undefined,
      });

      enqueueSnackbar(`Cleared approval level`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear approval level`, defaultSnackbarErrorProps);
      console.error('Error clearing approval level:', error);
    }
  }, [enqueueSnackbar, meetingId, meetingSession]);

  return (
    <GridContainer>
      <GridItem>
        <FormControl>
          <FormLabel>Approval Level</FormLabel>
          <RadioGroup
            name="approval-level-type-radio-group"
            value={statement ?? ''}
            onChange={(event) => setStatement(event.target.value as ApprovalLevelType)}
          >
            <FormControlLabel
              value={ApprovalLevelType.GloballyApproved}
              control={<Radio />}
              label={approvalLevelTypeLabel[ApprovalLevelType.GloballyApproved]}
            />
            <FormControlLabel
              value={ApprovalLevelType.NeedsRatification}
              control={<Radio />}
              label={approvalLevelTypeLabel[ApprovalLevelType.NeedsRatification]}
            />
          </RadioGroup>
        </FormControl>
      </GridItem>
      <GridItem className={commonStyles.buttonGroup}>
        <Button variant="contained" color="secondary" onClick={handleDisplayApprovalLevel}>
          Display Approval Level
        </Button>
        <Button variant="contained" color="error" onClick={handleClearApprovalLevel}>
          Clear Approval Level
        </Button>
      </GridItem>
    </GridContainer>
  );
};

export default ApprovalLevelSection;
